import { useMemo } from "react";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { ClientApiError, useApiConfig, type ApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { listReportsEndpoint, listScopedRoadsenseReportsEndpoint } from "@visioni/a3s-reports/client-messages";
import { useTenant, useTenantId } from "@src/features/tenants";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { listRowFromReport, listRowFromScopedReport, type RoadSenseListRow } from "../site-list/site-rows";
import { isNoReportAccess } from "./roadsense-report-errors";

/**
 * Lists the tenant's RoadSense reports newest-first — an **independent** copy
 * of the WasteSense listing hook (not shared) that leans only on the generic
 * endpoint + auth/tenant plumbing, with `type: "roadsense"`.
 *
 * Behind `roadsense_camera_access` it reads the camera-scoped RoadSense list
 * instead, which carries only the caller's in-scope sites. Either way the rows
 * come back as {@link RoadSenseListRow}s, so the screen has one shape. Each
 * route has its own query key, so a flag flip never serves one route's shape
 * from the other's cache (the cache is persisted across launches).
 *
 * Returns **all** statuses (no `status` filter). We deliberately surface
 * `generating`/`failed` rows so a report that's mid-(re)generation isn't
 * silently dropped — otherwise a tenant whose only report is regenerating sees
 * a misleading "No reports yet". The site list keeps a lifecycle's sites
 * clickable at any status, since the live site report serves a regenerating
 * report; only a lifecycle with no per-site list yet (never generated) renders
 * as a non-clickable placeholder with a status-aware sub-label.
 *
 * Pagination is cursor-based: the endpoint forwards the DDB `LastEvaluatedKey`
 * as an opaque base64 `cursor`, which `useInfiniteQuery` threads through
 * subsequent fetches. Cursor-based (vs offset) keeps the list stable when the
 * generator stamps a new row at the front mid-scroll.
 */
const STALE_TIME_MS = 60_000;

/**
 * Page size. The endpoint caps at 100; 52 keeps a year's worth of weekly rows
 * on a single page so steady-state tenants never have to "Load more".
 */
const PAGE_LIMIT = 52;

export interface UseRoadSenseReportListResult {
  readonly reports: readonly RoadSenseListRow[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  /** The camera-scoped list answered 403: the caller has no access in the tenant. Always false with the flag off. */
  readonly hasNoReportAccess: boolean;
  readonly isTenantPending: boolean;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => void;
}

export function useRoadSenseReportList(): UseRoadSenseReportListResult {
  const apiConfig = useApiConfig();
  const tenantId = useTenantId();
  const { isLoading: isTenantLoading } = useTenant();
  const cameraScoped = useBooleanFlag(FEATURE_FLAGS.ROADSENSE_CAMERA_ACCESS);

  const tenant = apiConfig.tenant;
  const enabled = !!tenant;

  // Both are always mounted (hooks can't be conditional); only the flag's route
  // is enabled and read.
  const tenantAdminList = useReportListPages({
    queryKey: ["roadsense", "reports-list", tenant?.tenantId, "all", PAGE_LIMIT],
    enabled: enabled && !cameraScoped,
    fetchPage: async (pageParam, signal) => {
      // `enabled` gates the call; this is a defensive narrow for the type system
      // since `tenant` is optional on `ApiConfig`.
      if (!tenant) throw new Error("Tenant not ready");
      const url =
        tenant.apiBaseUrl +
        expandEndpoint(listReportsEndpoint, {
          path: { tenantId: tenant.tenantId, type: "roadsense" },
          query: pageQuery(pageParam),
        });
      return listReportsEndpoint.responseSchema.parse(await fetchListPage(apiConfig, url, signal));
    },
  });

  const scopedList = useReportListPages({
    queryKey: ["roadsense", "scoped-reports-list", tenant?.tenantId, "all", PAGE_LIMIT],
    enabled: enabled && cameraScoped,
    fetchPage: async (pageParam, signal) => {
      if (!tenant) throw new Error("Tenant not ready");
      const url =
        tenant.apiBaseUrl +
        expandEndpoint(listScopedRoadsenseReportsEndpoint, {
          path: { tenantId: tenant.tenantId },
          query: pageQuery(pageParam),
        });
      // A 403 stays the `ForbiddenError` `authenticatedFetch` throws, which the
      // app's default retry already skips.
      return listScopedRoadsenseReportsEndpoint.responseSchema.parse(await fetchListPage(apiConfig, url, signal));
    },
  });

  const tenantAdminData = tenantAdminList.data;
  const scopedData = scopedList.data;
  const reports = useMemo<readonly RoadSenseListRow[]>(
    () =>
      cameraScoped ?
        (scopedData?.pages.flatMap((page) => page.data.map(listRowFromScopedReport)) ?? [])
      : (tenantAdminData?.pages.flatMap((page) => page.data.map(listRowFromReport)) ?? []),
    [cameraScoped, scopedData, tenantAdminData],
  );

  const query = cameraScoped ? scopedList : tenantAdminList;
  return {
    reports,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : null,
    hasNoReportAccess: cameraScoped && query.isError && isNoReportAccess(query.error),
    isTenantPending: isTenantLoading || !tenantId,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
  };
}

interface ListPage {
  readonly data: readonly unknown[];
  readonly cursor?: string | undefined;
}

interface ReportListPagesOptions<TPage extends ListPage> {
  readonly queryKey: readonly unknown[];
  readonly enabled: boolean;
  readonly fetchPage: (pageParam: string | undefined, signal: AbortSignal) => Promise<TPage>;
}

/** One list route's paged query, with the paging, refetch and (default) retry rules both routes share. */
function useReportListPages<TPage extends ListPage>({ queryKey, enabled, fetchPage }: ReportListPagesOptions<TPage>) {
  return useInfiniteQuery<
    TPage,
    Error,
    InfiniteData<TPage, string | undefined>,
    readonly unknown[],
    string | undefined
  >({
    queryKey,
    enabled,
    staleTime: STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.cursor,
    queryFn: ({ pageParam, signal }) => fetchPage(pageParam, signal),
  });
}

/**
 * No `status` filter: we want generating/failed rows too so the list never
 * falsely reads as empty during a (re)generation.
 */
function pageQuery(cursor: string | undefined) {
  return { limit: PAGE_LIMIT, ...(cursor !== undefined && { cursor }) };
}

/** Fetches one list page's body, throwing a `ClientApiError` with the server's message on a non-2xx. */
async function fetchListPage(apiConfig: ApiConfig, url: string, signal: AbortSignal): Promise<unknown> {
  const response = await apiConfig.authenticatedFetch(url, { signal });
  if (!response.ok) {
    let message = `Request failed: ${String(response.status)}`;
    try {
      const text = await response.text();
      if (text) {
        try {
          const json = JSON.parse(text) as { message?: string };
          if (json.message) message = json.message;
          else message = text;
        } catch {
          message = text;
        }
      }
    } catch {
      // fall through with default message
    }
    throw new ClientApiError(response.status, message);
  }
  return response.json();
}
